import { useCallback, useState } from 'react'
import { Navigate, Outlet, useNavigate } from 'react-router-dom'
import { reportError } from '@/shared/infra/observability'
import { useAuth, useOrgContext } from '@/shared/ui/hooks'
import { AppShellSkeleton } from '@/shared/ui/layout/app-shell-skeleton'
import { Button } from '@/shared/ui/primitives'
import { AuthLayout } from './auth-layout'

const LOAD_FALLBACK_MESSAGE = "We couldn't load your account. Check your connection and try again."

function AccountLoadFailed() {
  const { refresh } = useOrgContext()
  const { signOut } = useAuth()
  const navigate = useNavigate()
  const [isRetrying, setIsRetrying] = useState(false)
  const [message, setMessage] = useState(LOAD_FALLBACK_MESSAGE)

  const handleRetry = useCallback(async () => {
    if (!refresh) return
    setIsRetrying(true)
    try {
      await refresh()
    } catch (err) {
      setMessage(reportError(err, { fallbackMessage: LOAD_FALLBACK_MESSAGE }))
    } finally {
      setIsRetrying(false)
    }
  }, [refresh])

  const handleSignOut = useCallback(async () => {
    await signOut()
    navigate('/sign-in', { replace: true })
  }, [navigate, signOut])

  return (
    <AuthLayout title="We couldn't load your account" subtitle={message}>
      <div data-testid="org-membership-load-failed" className="flex flex-col gap-2">
        <Button
          type="button"
          disabled={isRetrying}
          onClick={() => {
            void handleRetry()
          }}
          className="w-full"
        >
          {isRetrying ? 'Trying again…' : 'Try again'}
        </Button>
        <Button
          type="button"
          variant="ghost"
          onClick={() => {
            void handleSignOut()
          }}
          className="w-full"
        >
          Sign out
        </Button>
      </div>
    </AuthLayout>
  )
}

export function RequireOrgMembership() {
  const { memberships, isLoading, loadFailed } = useOrgContext()

  if (isLoading) {
    return <AppShellSkeleton testId="org-membership-loading" />
  }

  if (memberships.length === 0) {
    if (loadFailed) return <AccountLoadFailed />
    return <Navigate to="/no-access" replace />
  }

  return <Outlet />
}
